import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import skylarqLogo from '../../assets/skylarq_logo_small.png';
import { apiClient } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import type { InvitePreview } from '../../types/workspace';
import { getErrorDetail } from '../../utils/errorUtils';
import { isAwaitingSetup, isPortalAccount } from '../../utils/provisioning';
import { Button } from '../ui/Button';
import { Spinner } from '../ui/Spinner';

type DataChoice = 'bring' | 'fresh';

const DATA_OPTIONS: { value: DataChoice; title: string; description: string }[] = [
  {
    value: 'bring',
    title: 'Bring my data',
    description:
      'Everything you’ve already synced (email, Slack, meetings) moves into the shared workspace and becomes visible to the team. Duplicate customers are merged automatically.',
  },
  {
    value: 'fresh',
    title: 'Start fresh',
    description:
      'Join with a clean slate. Your personal workspace is kept aside — if you ever leave this team, you’ll return to it with your data intact.',
  },
];

const SUCCESS_TEXT: Record<'portal' | DataChoice, string> = {
  portal: 'You’ve joined the workspace. Your team is ready for you.',
  bring: 'You’ve joined the workspace and your data is being folded in — merged customer histories may take a few minutes to settle.',
  fresh: 'You’ve joined the workspace with a clean slate. Your personal workspace is saved for you.',
};

const DataChoicePicker: React.FC<{ choice: DataChoice; onChange: (next: DataChoice) => void }> = ({ choice, onChange }) => (
  <div className="mt-6 space-y-3" role="radiogroup" aria-label="Data choice">
    {DATA_OPTIONS.map(opt => (
      <button
        key={opt.value}
        id={`data-choice-${opt.value}`}
        role="radio"
        aria-checked={choice === opt.value}
        onClick={() => onChange(opt.value)}
        className={`w-full text-left rounded-lg border p-4 transition focus:outline-none focus:ring-2 focus:ring-primary-600 ${
          choice === opt.value
            ? 'border-primary-600 bg-primary-50'
            : 'border-neutral-200 hover:border-neutral-300'
        }`}
      >
        <div className="flex items-center gap-2">
          <span
            className={`h-4 w-4 rounded-full border flex items-center justify-center ${
              choice === opt.value ? 'border-primary-600' : 'border-neutral-300'
            }`}
          >
            {choice === opt.value && <span className="h-2 w-2 rounded-full bg-primary-600" />}
          </span>
          <span className="text-sm font-medium text-neutral-900">{opt.title}</span>
        </div>
        <p className="mt-1.5 ml-6 text-xs text-neutral-500 leading-relaxed">{opt.description}</p>
      </button>
    ))}
  </div>
);

/** What joining does to the caller's solo plan and credits, and whether that could be read. */
const useInvitePreview = (token: string | undefined) => {
  const [preview, setPreview] = useState<InvitePreview | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    apiClient.previewWorkspaceInvite(token).then(
      (loaded) => { if (!cancelled) setPreview(loaded); },
      () => { if (!cancelled) setFailed(true); },
    );
    return () => { cancelled = true; };
  }, [token]);

  return { preview, failed };
};

/**
 * Accept a workspace invite. Rendered inside ProtectedRoute, so the user is already
 * authenticated and the backend matches their verified email against the invite.
 *
 * Operators choose whether their personal workspace data comes with them. Portal
 * accounts get a single Join button. After a join the app reloads from the root so
 * the router reads the new workspace rather than the profile captured here.
 */
export const AcceptInvitePage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { user, userProfile } = useAuth();
  const [status, setStatus] = useState<'choice' | 'loading' | 'success' | 'error'>('choice');
  const [choice, setChoice] = useState<DataChoice>('bring');
  const [errorMsg, setErrorMsg] = useState('');
  const [notice, setNotice] = useState<string | null>(null);

  const portalUser = isPortalAccount(userProfile, user?.isAdmin);
  const awaitingSetup = isAwaitingSetup(userProfile, user?.isAdmin);
  const dataChoice: DataChoice = portalUser ? 'fresh' : choice;
  const { preview, failed: previewFailed } = useInvitePreview(token);
  const blocked = dataChoice === 'bring' ? preview?.blocked : null;
  const outcome = previewFailed
    ? 'We couldn’t check what joining changes for your plan and credits.'
    : blocked ?? preview?.[dataChoice];

  const accept = async () => {
    if (!token) return;
    setStatus('loading');
    try {
      const joined = await apiClient.acceptWorkspaceInvite(token, dataChoice);
      setNotice(joined.notice);
      setStatus('success');
    } catch (error) {
      console.error('Invite accept failed:', error);
      setErrorMsg(getErrorDetail(error, 'This invite could not be accepted.'));
      setStatus('error');
    }
  };

  const successText = notice ?? SUCCESS_TEXT[portalUser ? 'portal' : dataChoice];

  return (
    <div className="min-h-screen bg-neutral-50 flex flex-col items-center justify-center px-4">
      <div className="w-full max-w-md text-center">
        <div className="flex justify-center mb-8">
          <img src={skylarqLogo} alt="Skylarq" className="h-8" />
        </div>

        {status === 'choice' && (
          <div className="bg-white rounded-2xl border border-neutral-200 shadow-card p-8 text-left">
            <h1 className="text-lg font-semibold text-neutral-900 text-center">
              Join this workspace
            </h1>
            <p className="mt-2 text-sm text-neutral-500 text-center">
              {portalUser
                ? 'You’ve been invited to a team workspace on Skylarq.'
                : 'Before you join, choose what happens to the data in your personal workspace.'}
              {awaitingSetup && ' Anything you’ve already connected stays in your personal workspace.'}
            </p>
            {!portalUser && <DataChoicePicker choice={choice} onChange={setChoice} />}
            {outcome && (
              <p id="invite-outcome" className="mt-4 text-sm text-neutral-700" aria-live="polite">{outcome}</p>
            )}
            <Button
              variant="primary"
              size="md"
              className="mt-6 w-full"
              onClick={accept}
              disabled={(!preview && !previewFailed) || Boolean(blocked)}
              aria-describedby={outcome ? 'invite-outcome' : undefined}
            >
              Join workspace
            </Button>
          </div>
        )}

        {status === 'loading' && <Spinner size="md" text="Joining the workspace…" center />}

        {status === 'success' && (
          <div className="bg-white rounded-2xl border border-neutral-200 shadow-card p-8">
            <h1 className="text-lg font-semibold text-neutral-900">You're in 🎉</h1>
            <p className="mt-2 text-sm text-neutral-500">{successText}</p>
            <Button
              variant="primary"
              size="md"
              className="mt-6"
              onClick={() => window.location.assign('/')}
            >
              Continue
            </Button>
          </div>
        )}

        {status === 'error' && (
          <div className="bg-white rounded-2xl border border-neutral-200 shadow-card p-8">
            <h1 className="text-lg font-semibold text-neutral-900">Invite couldn't be accepted</h1>
            <p className="mt-2 text-sm text-neutral-500">{errorMsg}</p>
            <p className="mt-2 text-xs text-neutral-400">
              Make sure you're signed in with the email the invite was sent to. Ask the sender to
              re-invite you if the link has expired.
            </p>
            <Button variant="secondary" size="md" className="mt-6" onClick={() => navigate('/')}>
              Back to Skylarq
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};
